import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlacesAutocomplete } from "../../component/Common/PlacesAutoComplete";
import { useMapsService } from "../../hooks/useMapsService";
import { LOADING_STATUS } from "../../constants/constant";
import { createEnquiry } from "../../api/Customer/enquiry";
import useToast from "../../hooks/useToast";

export const CheckFareAndBook = () => {
  const { ToastMessage, handleOpenToast } = useToast();
  const [navbarHeight, setNavbarHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const navbarElement = document.getElementById("landing-page-navbar");
      if (navbarElement) {
        const height = navbarElement.getBoundingClientRect().height;
        setNavbarHeight(height);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const navigate = useNavigate();
  const { isLoaded, getLatLng, calculateDistance, maskedAddressHelper } =
    useMapsService();
  const [errField, setErrorField] = useState({
    pickup: false,
    dropoff: false,
  });

  const [pickUpLocation, setPickupLocation] = useState({
    place: "",
    placeId: "",
  });
  const [dropOffLocation, setDropOffLocation] = useState({
    place: "",
    placeId: "",
  });

  const [btnStatus, setBtnStatus] = useState(LOADING_STATUS.INITIAL);

  const handleCreateEnquiry = async (
    pickupLocationDetails,
    dropoffLocationDetails,
    distance
  ) => {
    const maskedPickup = await maskedAddressHelper(
      pickUpLocation.placeId,
      pickUpLocation.place
    );
    const maskedDropOff = await maskedAddressHelper(
      dropOffLocation.placeId,
      dropOffLocation.place
    );
    const pickup = {
      location: {
        ...pickupLocationDetails,
        address: pickUpLocation.place,
        primary_text: maskedPickup.mainText,
        secondary_text: maskedPickup.secondaryText,
        isHaveCompanyAddress: maskedPickup.doesHaveCompanyAddress,
      },
    };
    const dropoff = {
      location: {
        ...dropoffLocationDetails,
        address: dropOffLocation.place,
        primary_text: maskedDropOff.mainText,
        secondary_text: maskedDropOff.secondaryText,
        isHaveCompanyAddress: maskedDropOff.doesHaveCompanyAddress,
      },
    };

    const enquiryBody = {
      pickup,
      dropoff,
      distance: parseInt(distance, 10),
    };
    try {
      const response = await createEnquiry(enquiryBody);
      const enquiryId = response.data.data.enquiryId;
      return enquiryId;
    } catch (err) {
      return undefined;
    }
  };

  const handleCheckFareAndBooktButtonClick = async () => {
    if (!isLoaded) return;

    if (!pickUpLocation.place || !dropOffLocation.place) return;
    if (pickUpLocation.placeId === dropOffLocation.placeId) {
      handleOpenToast("Pickup and Dropoff location cannot be same.", "error");
      return;
    }

    setBtnStatus(LOADING_STATUS.LOADING);
    try {
      const [pickupLocationDetails, dropoffLocationDetails, distance] =
        await Promise.all([
          getLatLng(pickUpLocation.placeId),
          getLatLng(dropOffLocation.placeId),
          calculateDistance(pickUpLocation.place, dropOffLocation.place),
        ]);

      try {
        const enqId = await handleCreateEnquiry(
          pickupLocationDetails,
          dropoffLocationDetails,
          distance
        );
        setBtnStatus(LOADING_STATUS.LOADED);
        navigate("/enquiry", {
          state: {
            detail: {
              source: pickUpLocation.place,
              destn: dropOffLocation.place,
              distance,
              enquiryId: enqId,
            },
          },
        });
      } catch (error) {
        setBtnStatus(LOADING_STATUS.FAILED);
      }
    } catch (error) {
      if (error.message.split(" ")[2] === pickUpLocation.placeId) {
        setErrorField((p) => ({ ...p, pickup: true }));
      } else if (error.message.split(" ")[2] === dropOffLocation.placeId) {
        setErrorField((p) => ({ ...p, dropoff: true }));
      } else {
        console.error(error);
      }
      setBtnStatus(LOADING_STATUS.FAILED);
    }
  };

  return (
    <>
      <ToastMessage />
      <Box sx={{ background: "#404750" }}>
        <Box
          sx={{
            backgroundImage: "url('assets/images/bgImage.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            minHeight: `calc(100vh - ${navbarHeight}px - 40px)`,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            maxWidth="xl"
            sx={{
              margin: "auto",
              width: "100%",
              px: { xs: 2, sm: 4, md: 6, lg: 10, xl: 16 },
              py: { xs: 6, sm: 8 },
              display: "flex",
              justifyContent: { xs: "center", lg: "flex-start" },
            }}
          >
            <Stack
              direction="column"
              spacing={{ xs: 3, sm: 4 }}
              sx={{ maxWidth: { xs: "600px", md: "480px" }, width: "100%" }}
            >
              <Typography
                color="#FFF"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  fontWeight: "bold",
                  lineHeight: 1.2,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                India's First Steel Logistics Solution
              </Typography>
              <Typography
                color="#FFF"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                For effortless movement of goods using simple technology.
              </Typography>
              <Box
                sx={{
                  background: "#FFF",
                  width: "100%",
                  maxWidth: { sm: "24rem", md: "26rem" },
                  mx: { xs: "auto", md: 0 },
                  mt: { xs: 1, md: 2 },
                  pt: 3,
                  borderRadius: 1,
                  px: { xs: 2.5, sm: 3 },
                  pb: 3,
                }}
              >
                <Typography
                  mb={1}
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1.125rem", sm: "1.25rem" },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Book Your Truck
                </Typography>
                <Stack direction="column" spacing={1.5}>
                  <PlacesAutocomplete
                    error={errField.pickup}
                    helperText={
                      errField.pickup && "Please pick more specific location"
                    }
                    resetError={() =>
                      setErrorField((p) => ({ ...p, pickup: false }))
                    }
                    placeholderText="Enter pick up location"
                    onPlaceSelect={(place, placeId) =>
                      setPickupLocation({ place, placeId })
                    }
                  />

                  <PlacesAutocomplete
                    error={errField.dropoff}
                    helperText={
                      errField.dropoff && "Please pick more specific location"
                    }
                    resetError={() =>
                      setErrorField((p) => ({ ...p, dropoff: false }))
                    }
                    placeholderText="Enter drop off location"
                    onPlaceSelect={(place, placeId) => {
                      setDropOffLocation({ place, placeId });
                    }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    disabled={btnStatus === LOADING_STATUS.LOADING}
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                      py: { xs: 1, sm: 1.25 },
                    }}
                    onClick={handleCheckFareAndBooktButtonClick}
                  >
                    CHECK FARE & BOOK
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{ background: "#414A50", textAlign: "center", marginTop: "auto" }}
      >
        <Typography
          color="#FFF"
          fontSize={{ xs: "0.875rem", sm: "1rem", md: "1.125rem" }}
          fontWeight="700"
          px={{ xs: 2, sm: 4 }}
          py={{ xs: 1, sm: 1.5 }}
        >
          We operate in Bengaluru and nearby regions, while expanding our
          services across India!
        </Typography>
      </Box>
    </>
  );
};
